import { useState, useRef } from 'react';
import './report-input.scss';

interface ReportInputProps {
  onSubmit: (reportNumber: string) => Promise<void>;
  language?: 'ar' | 'en' | 'ur';
}

export default function ReportInput({ onSubmit, language = 'ar' }: ReportInputProps) {
  const [reportNumber, setReportNumber] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const texts = {
    ar: {
      title: 'تجديد تقرير حادث',
      placeholder: 'أدخل رقم التقرير',
      button: 'بحث',
      loading: 'جاري البحث...',
      errorInvalid: 'الرجاء إدخال رقم تقرير صحيح',
      errorNotFound: 'التقرير غير موجود. يرجى التحقق من الرقم والمحاولة مرة أخرى',
      errorNetwork: 'حدث خطأ في الاتصال. يرجى المحاولة مرة أخرى',
    },
    en: {
      title: 'Renew Accident Report',
      placeholder: 'Enter Report Number',
      button: 'Search',
      loading: 'Searching...',
      errorInvalid: 'Please enter a valid report number',
      errorNotFound: 'Report not found. Please check the number and try again',
      errorNetwork: 'Connection error occurred. Please try again',
    },
    ur: {
      title: 'رپورٹ کی تجدید',
      placeholder: 'رپورٹ نمبر درج کریں',
      button: 'تلاش کریں',
      loading: 'تلاش جاری ہے...',
      errorInvalid: 'براہ کرم درست رپورٹ نمبر درج کریں',
      errorNotFound: 'رپورٹ نہیں ملی۔ براہ کرم نمبر چیک کریں اور دوبارہ کوشش کریں',
      errorNetwork: 'کنکشن میں خرابی پیش آئی۔ براہ کرم دوبارہ کوشش کریں',
    }
  };

  const t = texts[language];

  const validateReportNumber = (value: string): boolean => {
    // Basic validation - report numbers are typically numeric
    return value.trim().length > 0 && /^\d+$/.test(value.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateReportNumber(reportNumber)) {
      setError(t.errorInvalid);
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit(reportNumber.trim());
    } catch (err: any) {
      console.error('Error submitting report number:', err);
      if (err.message?.includes('not found') || err.message?.includes('404')) {
        setError(t.errorNotFound);
      } else {
        setError(t.errorNetwork);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReportNumber(e.target.value);
    if (error) setError(''); // Clear error when user types
  };

  return (
    <div className="report-input-container">
      <div className="report-input-header">
        <h3>{t.title}</h3>
      </div>

      <form onSubmit={handleSubmit} className="report-input-form">
        <div className="input-group">
          <input
            ref={inputRef}
            type="text"
            value={reportNumber}
            onChange={handleInputChange}
            placeholder={t.placeholder}
            disabled={isLoading}
            className={error ? 'error' : ''}
            autoFocus
          />
          <button 
            type="submit"
            className="submit-button"
            disabled={isLoading || !reportNumber.trim()}
          >
            {isLoading ? t.loading : t.button}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
        )}
      </form>
    </div>
  );
}

